/**
 * PropertyInformationPanel — native rewrite of the legacy PropertyInformation sub-tab.
 *
 * Lifts the design-system markup verbatim; rewrites data wiring onto the module ops
 * hook + codec cross-derived setters. The three highest-risk fields (residentialUnit,
 * constructionYear, energetic conditions, isConstructionConfirmed) go through
 * `applyDerived` → the codec's field-granular `patch*` setters, which recompute their
 * declared cross-derived siblings (livingUnitsDescr / buildingPeriod /
 * energeticRenovations) from the LIVE DTO (C1).
 *
 * The numeric fields keep what the client typed (local strings for the years and the
 * commercial area, `NumericFloatField` for the living space) while every keystroke commits
 * the parsed value to the DTO, which stays authoritative.
 */

import React, { useMemo, useState } from 'react'
import { Box, Fade, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

import { GRAY_700, GRAY_900 } from 'constants/styling/theme'
import {
  BuildingLocation,
  ConsumptionCertificateBasementType,
  ConsumptionCertificateVentilationType
} from 'constants/purchaseFlow/consumptionCertificateTypes'
import { BuildingType } from 'constants/purchaseFlow/sharedEnergyCertificateTypes'
import { DropdownInputTypes, MUIDropdownInput } from 'components/common/MUIDropdownInput/MUIDropdownInput.component'
import { Label } from 'components/common/Label'
import { MUICheckbox } from 'components/common/MUICheckBox'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import { NumericFloatField } from 'components/common/MUIFloatNumberField'
import { toOptionalNumber } from 'utils/formatting/numberUtils'
import { MUIInputField } from 'components/common/MUIInputField'
import { MUIInputFieldCounter } from 'components/common/MUIInputFieldCounter'
import { MUIRadio } from 'components/common/MUIRadio'
import { MUITooltip } from 'components/common/MUITooltip'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import { SystemMessage } from 'components/common/SystemMessage'

import type { CartItemId } from 'models/purchaseFlowRevamp'

import { useConsumptionCertificateConfig } from '../useConsumptionCertificateConfig'
import {
  patchBuildingType,
  patchConstructionYear,
  patchIsConstructionConfirmed,
  patchResidentialUnit
} from '../consumptionCertificate.codec'
import { isBuildingTypeAllowed } from '../consumptionCertificate.dto'

const InfoInputWidth = { xs: '100%', sm: '25rem' }

export interface PropertyInformationPanelProps {
  cartItemId: CartItemId
}

export const PropertyInformationPanel: React.FC<PropertyInformationPanelProps> = ({ cartItemId }) => {
  const { t } = useTranslation(['purchase_flow'], { keyPrefix: 'consumption_certificate_step.property_information_page' })
  const { value, patch, applyDerived } = useConsumptionCertificateConfig(cartItemId)
  const livingUnits = value.livingUnits ?? 1
  // A draft can carry a type its unit count no longer allows: shown as unselected and flagged, never rewritten on mount.
  const buildingTypeFits = !value.buildingType || isBuildingTypeAllowed(value.buildingType, livingUnits)

  const [showError, setShowError] = useState(false)
  const handleOnInputBlur = () => {
    if (!showError) setShowError(true)
  }

  // Leaf-local mid-edit strings (seeded once from the persisted DTO).
  const [constructionYear, setConstructionYear] = useState(() => (value.buildingYear ? String(value.buildingYear) : ''))
  const [yearOfRenovation, setYearOfRenovation] = useState(() => (value.renovationYear ? String(value.renovationYear) : ''))
  const [commercialArea, setCommercialArea] = useState(() =>
    value.commercialSpaceInSquareMeters ? String(value.commercialSpaceInSquareMeters) : ''
  )

  // Pre-1978 construction note (legacy `isShowConstructionNote`, computed from the
  // settled DTO — no debounce needed, behaviour-equivalent at rest).
  const isShowConstructionNote = useMemo(() => {
    const yearStr = value.buildingYear ? String(value.buildingYear) : ''
    return (value.livingUnits ?? 1) < 5 && (value.buildingYear ?? 0) < 1978 && yearStr.length >= 4
  }, [value.livingUnits, value.buildingYear])

  const handleConstructionYearChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '')
    setConstructionYear(digits)
    applyDerived((c) => patchConstructionYear(c, digits))
  }

  const handleRenovationYearChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '')
    setYearOfRenovation(digits)
    patch({ renovationYear: toOptionalNumber(digits) })
  }

  const handleCommercialAreaChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '')
    setCommercialArea(digits)
    patch({ commercialSpaceInSquareMeters: toOptionalNumber(digits) }) // MIXED-only field
  }

  return (
    <SectionedBorderBox title={t('title')}>
      <Stack gap="1.8rem" width={{ xs: '100%', sm: '40rem', lg: '50rem' }} position="relative">

        {/* Building type */}
        <MUIDropdown
          sx={{ width: '100%' }}
          button={(_, action) => (
            <MUIDropdownInput
              readOnly
              required
              label={t('property_type')}
              type={DropdownInputTypes.SELECTOR}
              value={t(value.buildingType && buildingTypeFits ? `building_type.${value.buildingType}` : 'select_building_type_option')}
              isError={!buildingTypeFits}
              onClick={action}
            />
          )}
        >
          {Object.values(BuildingType).filter((type) => isBuildingTypeAllowed(type, livingUnits)).map((type) => (
            <MUIDropdownItem key={type} onClick={() => applyDerived((c) => patchBuildingType(c, type))}>
              <Typography variant="text-md" color={GRAY_900} fontWeight="medium">
                {t(`building_type.${type}`)}
              </Typography>
            </MUIDropdownItem>
          ))}
        </MUIDropdown>
        {livingUnits >= 5 &&
          <Typography variant="text-sm" color={buildingTypeFits ? GRAY_700 : 'error.main'} marginTop="-1.2rem">
            {t('building_type_five_or_more_hint')}
          </Typography>
        }

        {/* Building location */}
        <MUIDropdown
          sx={{ width: '100%' }}
          button={(_, action) => (
            <MUIDropdownInput
              readOnly
              required
              label={t('building_location')}
              type={DropdownInputTypes.SELECTOR}
              value={t(
                value.buildingLocation ? `building_location_type.${value.buildingLocation}` : 'select_building_location_option'
              )}
              onClick={action}
            />
          )}
        >
          {Object.values(BuildingLocation).map((type) => (
            <MUIDropdownItem key={type} onClick={() => patch({ buildingLocation: type })}>
              <Typography variant="text-md" color={GRAY_900} fontWeight="medium">
                {t(`building_location_type.${type}`)}
              </Typography>
            </MUIDropdownItem>
          ))}
        </MUIDropdown>

        {/* Residential units */}
        <MUIInputFieldCounter
          minValue={1}
          counterValue={livingUnits}
          counterOnChange={(n) => applyDerived((c) => patchResidentialUnit(c, n))}
          inputBoxWidth="14rem"
          label={t('residential_units')}
        />

        {/* Commercial area — MIXED building only */}
        {value.buildingType === BuildingType.MIXED_USE_BUILDING && <>

          <Box width={InfoInputWidth}>
            <MUIInputField
              required
              label={t('commercial_area')}
              value={commercialArea}
              isError={showError && !commercialArea}
              onBlur={handleOnInputBlur}
              onChange={(e) => handleCommercialAreaChange(e.target.value)}
            />
          </Box>

          <Box width={InfoInputWidth}>
            <MUIInputField
              required
              label={t('commercial_area_type')}
              value={value.typeOfUseCommercial ?? ''}
              isError={showError && !value.typeOfUseCommercial}
              onChange={(e) => patch({ typeOfUseCommercial: e.target.value })}
              onBlur={handleOnInputBlur}
            />
          </Box>

        </>}

        {/* Construction year */}
        <Box width={InfoInputWidth}>
          <MUIInputField
            required
            label={t('construction_year')}
            value={constructionYear}
            isError={showError && (!constructionYear || constructionYear.length < 4)}
            onBlur={handleOnInputBlur}
            onChange={(e) => handleConstructionYearChange(e.target.value)}
          />
        </Box>

        {isShowConstructionNote &&
          <Fade in={isShowConstructionNote}>
            <Stack gap="1.6rem">

              <SystemMessage
                variant="warning"
                title={t('construction_note_title')}
                message={t('construction_note_content')}
              />

              <MUICheckbox
                label={t('construction_note_confirm')}
                checked={!!value.isConstructionConfirmed}
                onClick={() => applyDerived((c) => patchIsConstructionConfirmed(c, !c.isConstructionConfirmed))}
              />

            </Stack>
          </Fade>
        }

        {/* Living space */}
        <Box width={InfoInputWidth}>
          <NumericFloatField
            required
            label={t('living_space')}
            suffix="m2"
            tooltip={
              <MUITooltip content={t('living_area_tooltip')}>
                <InfoOutlinedIcon fontSize="medium" sx={{ paddingTop: '.4rem' }} />
              </MUITooltip>
            }
            value={value.totalLivingArea ? String(value.totalLivingArea) : ''}
            isError={showError && !value.totalLivingArea}
            onBlur={handleOnInputBlur}
            onCommit={(raw) => patch({ totalLivingArea: toOptionalNumber(raw) })}
          />
        </Box>

        {/* Year of renovation */}
        <Box width={InfoInputWidth}>
          <MUIInputField
            label={t('year_of_renovation')}
            value={yearOfRenovation}
            isError={!!yearOfRenovation && yearOfRenovation.length < 4}
            onBlur={handleOnInputBlur}
            onChange={(e) => handleRenovationYearChange(e.target.value)}
          />
        </Box>

        {/* Basement */}
        <Stack gap=".6rem">

          <Label text={t('basement')} />

          {Object.values(ConsumptionCertificateBasementType).map((type) => (
            <Stack key={type} gap=".8rem" flexDirection="row" alignItems="center">

              <MUIRadio checked={value.basement === type} onChange={() => patch({ basement: type })} />

              <Typography
                variant="text-md"
                fontWeight={500}
                color={GRAY_900}
                sx={{ cursor: 'pointer' }}
                onClick={() => patch({ basement: type })}
              >
                {t(`basement_type.${type}`)}
              </Typography>

            </Stack>
          ))}

        </Stack>

        {/* Ventilation */}
        <Stack gap=".6rem">

          <Label text={t('ventilation')} />

          {Object.values(ConsumptionCertificateVentilationType).map((type) => (
            <Stack key={type} gap=".8rem" flexDirection="row" alignItems="center">

              <MUIRadio checked={value.ventilation === type} onChange={() => patch({ ventilation: type })} />

              <Typography
                variant="text-md"
                fontWeight={500}
                color={GRAY_900}
                sx={{ cursor: 'pointer' }}
                onClick={() => patch({ ventilation: type })}
              >
                {t(`ventilation_type.${type}`)}
              </Typography>

            </Stack>
          ))}

        </Stack>

      </Stack>
    </SectionedBorderBox>
  )
}
